import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get } from 'lodash';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { TrackingHandler } from 'client/tracking/handler';
import { CommonMap } from 'client/tracking/maps/common';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { buildVehiclePath, VehicleEntities, VehicleModel } from 'client/data/models/vehicle-v2';
import { buildLongTermRoadTestPath, LongTermRoadTestModel, LtrtEntities } from 'client/data/models/long-term-road-test';
import { buildMakeModelYearMediumEvoxPhotoPath, PhotosModel } from 'client/data/models/photos';
import { NO_VEHICLE_GRAY_IMAGE_SEDAN } from 'site-modules/shared/constants/images';
import { buildLtrtBreadcrumbs } from 'site-modules/long-term-road-test/utils/road-tests-breadcrumbs-builder';
import { getMediaImageUrl, getStaticImageUrl } from 'client/utils/image-helpers';
import { renderArticles } from 'site-modules/long-term-road-test/utils/article-builder';
import { AdUnit } from 'site-modules/shared/components/ad-unit/ad-unit';
import { IncentivesAd } from 'site-modules/shared/components/native-ad/incentives-ad/incentives-ad';
import { Breadcrumbs } from 'site-modules/shared/components/breadcrumbs/breadcrumbs';
import { LtrtSummary } from 'site-modules/long-term-road-test/components/ltrt-summary/ltrt-summary';

import './long-term-road-test.scss';

function getImageUrl(defaultPhoto) {
  return defaultPhoto ? getMediaImageUrl(defaultPhoto) : getStaticImageUrl(NO_VEHICLE_GRAY_IMAGE_SEDAN);
}

function LtrtArticlesUI({ params, vehicle, ltrtContent, isMobile, breakpoint, pathname, defaultPhoto }) {
  useEffect(() => {
    TrackingHandler.useMap(CommonMap);
  }, []);

  const ltrtVehicle = vehicle || get(ltrtContent, 'vehicle');
  if (!ltrtVehicle || !ltrtContent) {
    return null;
  }

  const { title, subtitle, articles, bullets } = ltrtContent;
  const leadImage = get(ltrtContent, 'dataPoints.leadImage');

  const imageUrl = leadImage || getImageUrl(defaultPhoto);
  const imageTitle = `${get(ltrtVehicle, 'make.name', '')} ${get(ltrtVehicle, 'model.name', '')} ${get(
    ltrtVehicle,
    'year',
    ''
  )}`;
  const heroImage = { imageUrl, imageTitle };
  const hasLtrtSummary = bullets && bullets.value.length > 0;
  const isMediumScreenDown = ['md', 'sm', 'xs'].includes(breakpoint);
  const breadcrumbs = buildLtrtBreadcrumbs({
    pathname,
    vehicle: ltrtVehicle,
    hasSubmodel: !!params.submodel,
    hasVehicleData: !!vehicle,
  });

  return (
    <main id="main-content" className="ltrt-article">
      <Container>
        <Breadcrumbs className="ps-0" data={breadcrumbs} />
        <Row>
          <Col className="my-1 mb-md-1_5">
            <h1 className="heading-2">{title}</h1>
            {!!subtitle && <h2 className="heading-4 fw-normal">{subtitle}</h2>}
          </Col>
        </Row>
        {hasLtrtSummary && <LtrtSummary ltrtContent={ltrtContent} heroImage={heroImage} />}
        <Row className="pb-lg-2">
          <Col xs={12} lg={8} data-sticky-bottom-boundary>
            {!hasLtrtSummary && <img className="w-100 mb-1_5" alt={heroImage.imageTitle} src={heroImage.imageUrl} />}
            {articles && (
              <div className="px-md-2_5 px-lg-0 articles-content">
                {renderArticles(articles, isMobile, false, breakpoint)}
              </div>
            )}
            {isMediumScreenDown && (
              <div className="px-md-2_5 px-lg-0">
                <AdUnit
                  adName="mrect"
                  position={'1'}
                  nativeStyle="mediumrectangle"
                  refreshable={false}
                  renderWhenViewable={false}
                  md
                  sm
                  xs
                />
              </div>
            )}
          </Col>
          {!isMediumScreenDown && (
            <Col xs={12} lg={4}>
              <div className="right-rail-sticky-wrapper pos-r">
                <Fragment>
                  <div className="top-page-mrect mt-2 mt-lg-0 px-md-2_5 px-lg-0">
                    <IncentivesAd mobile={isMobile} wrapperClass="mb-2 px-0" />
                  </div>
                  <div className="bottom-page-mrect">
                    <AdUnit
                      adName="mrect"
                      position={'1'}
                      nativeStyle="mediumrectangle"
                      renderWhenViewable={false}
                      refreshable={false}
                      lg
                      xl
                    />
                  </div>
                </Fragment>
              </div>
            </Col>
          )}
        </Row>
        <hr />
      </Container>
    </main>
  );
}

LtrtArticlesUI.propTypes = {
  params: PropTypes.shape({
    make: PropTypes.string,
    model: PropTypes.string,
    submodel: PropTypes.string,
    year: PropTypes.string,
  }).isRequired,
  vehicle: VehicleEntities.MakeModelSubmodelYear,
  ltrtContent: LtrtEntities.LtrtContent,
  isMobile: PropTypes.bool,
  breakpoint: PropTypes.string,
  pathname: PropTypes.string,
  defaultPhoto: PropTypes.string,
};

LtrtArticlesUI.defaultProps = {
  vehicle: null,
  ltrtContent: null,
  isMobile: false,
  breakpoint: '',
  pathname: null,
  defaultPhoto: null,
};

const mapStateToProps = state => ({
  isMobile: state.mobile,
  breakpoint: get(state, 'pageContext.breakpointDeprecated'),
  pathname: get(state, 'pageContext.location.pathname'),
});

export const LtrtArticles = connect(mapStateToProps)(
  connectToModel(LtrtArticlesUI, {
    ltrtContent: bindToPath(({ params }) => buildLongTermRoadTestPath(params), LongTermRoadTestModel),
    vehicle: bindToPath(({ params }) => buildVehiclePath(params), VehicleModel),
    defaultPhoto: bindToPath(({ params }) => buildMakeModelYearMediumEvoxPhotoPath(params), PhotosModel),
  })
);
